/**
 * AdvisorCard Component (T047 - US3)
 * Displays individual advisor information in search results
 *
 * Features:
 * - Shows advisor name, company name, and profile photo
 * - Fallback to initials avatar when no photo available
 * - Clickable card that triggers selection callback
 *
 * Styling based on docs/prototypes/SelectAdvisorPage.tsx
 *
 * FR-018, FR-019
 */

import type { AdvisorInfo } from '../types';
import { Avatar, AvatarImage, AvatarFallback } from '@/shared/components/ui/avatar';

type AdvisorCardProps = {
  advisor: AdvisorInfo;
  onClick: (advisor: AdvisorInfo) => void;
};

/**
 * Get initials from first and last name
 */
const getInitials = (firstName: string, lastName: string): string => {
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
};

export const AdvisorCard = ({ advisor, onClick }: AdvisorCardProps) => {
  const handleClick = () => {
    onClick(advisor);
  };

  return (
    <div
      onClick={handleClick}
      className="flex items-center gap-4 p-4 font-medium cursor-pointer hover:bg-gray-100 rounded-md my-1 transition-colors w-full min-w-0"
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
    >
      <Avatar className="h-16 w-16 flex-shrink-0">
        {advisor.logoOrHeadshot && (
          <AvatarImage
            src={advisor.logoOrHeadshot}
            alt={`${advisor.firstName} ${advisor.lastName}`}
          />
        )}
        <AvatarFallback className="bg-[#026857] text-white text-base font-medium">
          {getInitials(advisor.firstName, advisor.lastName)}
        </AvatarFallback>
      </Avatar>
      <div className="flex-1 text-left min-w-0">
        <p className="font-semibold text-gray-600 text-lg truncate">
          {advisor.firstName} {advisor.lastName}
        </p>
        {advisor.companyName && (
          <p className="text-base text-gray-600 truncate">{advisor.companyName}</p>
        )}
      </div>
    </div>
  );
};
